// AlertCenter — floating toast-stack rendering active alerts from
// useAlertStore. Lives in the top-right of the map area so it doesn't
// fight with the airport detail panel (right side, lower) or the
// hazard / instrument bars (bottom). Severity drives color:
//
//   info       slate ring, calm text
//   warning    amber ring + amber title
//   critical   red ring + red title + subtle pulse animation
//
// Each alert can carry an action button; clicking it runs the\n// callback and dismisses.

import { useAlertStore, type AlertSeverity } from '../stores/alertStore';

const SEV_STYLES: Record<AlertSeverity, { ring: string; title: string; pulse: boolean }> = {
  info:     { ring: 'border-cockpit-500',    title: 'text-cockpit-100', pulse: false },
  warning:  { ring: 'border-amber-500',      title: 'text-amber-300',   pulse: false },
  critical: { ring: 'border-red-600',        title: 'text-red-400',     pulse: true  },
};

export default function AlertCenter() {
  const alerts = useAlertStore((s) => s.alerts);
  const dismiss = useAlertStore((s) => s.dismiss);

  if (alerts.length === 0) return null;

  return (
    <div
      className="absolute z-30 right-3 flex flex-col gap-2 pointer-events-none"
      style={{ top: 'calc(var(--safe-top) + 4rem)', maxWidth: 'min(22rem, calc(100vw - 1.5rem))' }}
      aria-live="polite"
      aria-atomic="false"
    >
      {alerts.map((a) => {
        const sty = SEV_STYLES[a.severity];
        return (
          <div
            key={a.id}
            className={[
              'pointer-events-auto bg-cockpit-800/95 backdrop-blur border rounded-xl shadow-bar',
              'px-3 py-2 transition-all',
              sty.ring,
              sty.pulse ? 'animate-pulse' : '',
            ].join(' ')}
            role="alert"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className={['text-[12px] font-semibold leading-tight', sty.title].join(' ')}>
                  {a.title}
                </div>
                {a.body && (
                  <div className="text-[11px] text-cockpit-200 leading-snug mt-0.5">{a.body}</div>
                )}
              </div>
              <button
                onClick={() => dismiss(a.id)}
                aria-label="Dismiss alert"
                className="text-cockpit-300 hover:text-cockpit-100 leading-none px-1"
              >✕</button>
            </div>
            {a.actionLabel && a.onAction && (
              <button
                onClick={() => { a.onAction?.(); dismiss(a.id); }}
                className="mt-1.5 px-2 py-1 rounded-md bg-cockpit-700 hover:bg-cockpit-600 active:bg-cockpit-500 border border-cockpit-600 text-[11px] text-cockpit-100"
              >
                {a.actionLabel}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
